import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '@store/store';
import { LANGUAGE, SEARCH_PARAMS } from '@constants/index';
import { l10nType } from '@type/index';

export interface UiState {
    serviceAlias: string;
    alias: string;
    l10n: l10nType | null;
    l10nStatus: 'idle' | 'pending' | 'success' | 'fail';
    keyword: string;
    isHistoryBack: boolean;
}

const params = new URLSearchParams(location.search);
const initialState: UiState = {
    serviceAlias: '', // 서비스 alias (lineage / lineage2 / ...)
    alias: '', // 탭/url 등 페이지 alias
    l10n: null,
    l10nStatus: 'idle',
    keyword: params.get(SEARCH_PARAMS.KEYWORD) || '',
    isHistoryBack: false
};

// getL10N
export const dynamicImportL10N = createAsyncThunk(
    'ui/setL10N',
    async () => {
        return await import(
            // webpack - magic comments (지우지 마세요)
            /* webpackInclude: /\.json$/,
                webpackMode: "lazy", 
                webpackChunkName: '[request]' */ 
            `/src/ts/common/l10n/${LANGUAGE}.json`)
            .then(res => res?.default);
    }
)

// 여러 동작 수행 후 한번에 searchParams 변경하고 싶을 때
export const taskBeforeSetParams = createAsyncThunk(
    'ui/taskBeforeSetParams',
    async (taskArray: Promise<any>[]) => {
        const result = await Promise.allSettled(taskArray);
        return result?.every(v => {
            return v?.status === 'fulfilled';
        });
    }
)

export const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        setServiceAlias(state, action) {
            state.serviceAlias = action.payload;
        },
        setAlias(state, action) {
            state.alias = action.payload;
        },
        setKeyword(state, action) {
            state.keyword = action.payload;
        },
        setIsHistoryBack(state, action) {
            state.isHistoryBack = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
        .addCase(dynamicImportL10N.pending, (state) => {
            state.l10nStatus = 'pending';
        })
        .addCase(dynamicImportL10N.fulfilled, (state, action) => {
            state.l10nStatus = 'success';
            state.l10n = action.payload;
        })
        .addCase(dynamicImportL10N.rejected, (state) => {
            state.l10nStatus = 'fail';
        })
    }
});

export const { 
    setServiceAlias,
    setAlias,
    setKeyword,
    setIsHistoryBack
} = uiSlice.actions;

export const selectServiceAlias = (state: RootState) => state?.ui?.serviceAlias;
export const selectAlias = (state: RootState) => state?.ui?.alias;
export const selectL10N = (state: RootState) => state?.ui?.l10n;
export const selectL10NStatus = (state: RootState) => state?.ui?.l10nStatus;
export const selectKeyword = (state: RootState) => state?.ui?.keyword;
export const selectIsHistoryBack = (state: RootState) => state?.ui?.isHistoryBack;

export default uiSlice.reducer;